import {apiFetch} from '../../security/apiFetch';
import {
Add,
Block,
CheckCircle,Delete,Edit,Person,Security
} from '@mui/icons-material';
import {
Alert,Avatar,Box,Button,Card,
CardContent,Chip,CircularProgress,Dialog,DialogActions,DialogContent,DialogTitle,FormControl,FormControlLabel,IconButton,InputLabel,MenuItem,Paper,Select,Switch,Table,
TableBody,
TableCell,
TableContainer,
TableHead,
TableRow,TextField,Tooltip,Typography
} from '@mui/material';
import React,{ useCallback,useEffect,useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';

interface User {
  id: string;
  username: string;
  email: string;
  password_hash?: string;
  telegram_id?: number;
  first_name?: string;
  last_name?: string;
  language_code?: string;
  locale?: string;
  timezone?: string;
  madhab?: string;
  is_premium: boolean;
  role: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const Users: React.FC = () => {
  const { token } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    role: '',
    is_active: true,
    first_name: '',
    last_name: '',
    telegram_id: '',
    locale: '',
    timezone: '',
    madhab: '',
    is_premium: false
  });



  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiFetch('/api/v1/users/admin/users', {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data || []);
      } else {
        setError('Не удалось загрузить список пользователей');
        setUsers([]);
      }
    } catch (err) {
      console.error('Ошибка загрузки пользователей:', err);
      setError('Не удалось загрузить список пользователей');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  },[token]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      role: user.role,
      is_active: user.is_active,
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      telegram_id: user.telegram_id?.toString() || '',
      locale: user.locale || '',
      timezone: user.timezone || '',
      madhab: user.madhab || '',
      is_premium: user.is_premium
    });
    setOpenDialog(true);
  };

  const handleCreateUser = () => {
    setEditingUser(null);
    setFormData({
      username: '',
      email: '',
      role: 'user',
      is_active: true,
      first_name: '',
      last_name: '',
      telegram_id: '',
      locale: 'ru',
      timezone: 'Europe/Moscow',
      madhab: 'hanafi',
      is_premium: false
    });
    setOpenDialog(true);
  };

  const handleSaveUser = async () => {
    try {
      setLoading(true);

      const userData = {
        username: formData.username,
        email: formData.email,
        role: formData.role,
        is_active: formData.is_active,
        first_name: formData.first_name || null,
        last_name: formData.last_name || null,
        telegram_id: formData.telegram_id ? parseInt(formData.telegram_id) : null,
        locale: formData.locale || null,
        timezone: formData.timezone || null,
        madhab: formData.madhab || null,
        is_premium: formData.is_premium
      };

      if (editingUser) {
        // Обновление существующего пользователя
        const response = await apiFetch(`/api/v1/users/admin/users/${editingUser.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(userData),
        });

        if (response.ok) {
          const updatedUser = await response.json();
          setUsers(users.map(user =>
            user.id === editingUser.id ? updatedUser : user
          ));
          setOpenDialog(false);
        } else {
          setError('Не удалось обновить пользователя');
        }
      } else {
        // Создание нового пользователя
        const response = await apiFetch('/api/v1/users/admin/users', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(userData),
        });

        if (response.ok) {
          const newUser = await response.json();
          setUsers([...users, newUser]);
          setOpenDialog(false);
        } else {
          setError('Не удалось создать пользователя');
        }
      }
    } catch (err) {
      console.error('Ошибка сохранения пользователя:', err);
      setError('Ошибка при сохранении пользователя');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (window.confirm('Вы уверены, что хотите удалить этого пользователя?')) {
      try {
        const response = await apiFetch(`/api/v1/users/admin/users/${userId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` },
        });

        if (response.ok) {
          setUsers(users.filter(user => user.id !== userId));
        } else {
          setError('Не удалось удалить пользователя');
        }
      } catch (err) {
        console.error('Ошибка удаления пользователя:', err);
        setError('Не удалось удалить пользователя');
      }
    }
  };

  const handleToggleUserStatus = async (userId: string, currentStatus: boolean) => {
    const newStatus = !currentStatus;

    try {
      const response = await apiFetch(`/api/v1/users/admin/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ is_active: newStatus }),
      });

      if (response.ok) {
        setUsers(users.map(user =>
          user.id === userId ? { ...user, is_active: newStatus } : user
        ));
      } else {
        setError('Не удалось изменить статус пользователя');
      }
    } catch (err) {
      console.error('Ошибка изменения статуса пользователя:', err);
      setError('Не удалось изменить статус пользователя');
    }
  };

  const getStatusColor = (isActive: boolean) => {
    return isActive ? 'success' : 'error';
  };

  const getStatusText = (isActive: boolean) => {
    return isActive ? 'Активен' : 'Заблокирован';
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'error';
      case 'moderator': return 'warning';
      case 'user': return 'primary';
      default: return 'default';
    }
  };

  const getRoleText = (role: string) => {
    switch (role) {
      case 'admin': return 'Администратор';
      case 'moderator': return 'Модератор';
      case 'user': return 'Пользователь';
      default: return role;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ru-RU');
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          Управление пользователями
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleCreateUser}
        >
          Добавить пользователя
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Статистика */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, mb: 4 }}>
        <Box sx={{ flex: "1 1 300px", minWidth: "300px" }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Person sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Всего пользователей</Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {users.length}
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: "1 1 300px", minWidth: "300px" }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CheckCircle sx={{ mr: 1, color: 'success.main' }} />
                <Typography variant="h6">Активные</Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {users.filter(u => u.is_active).length}
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: "1 1 300px", minWidth: "300px" }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Block sx={{ mr: 1, color: 'error.main' }} />
                <Typography variant="h6">Заблокированные</Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {users.filter(u => !u.is_active).length}
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: "1 1 300px", minWidth: "300px" }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Security sx={{ mr: 1, color: 'warning.main' }} />
                <Typography variant="h6">Администраторы</Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {users.filter(u => u.role === 'admin').length}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Таблица пользователей */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Список пользователей
          </Typography>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Пользователь</TableCell>
                    <TableCell>Роль</TableCell>
                    <TableCell>Статус</TableCell>
                    <TableCell>Последнее обновление</TableCell>
                    <TableCell>Дата регистрации</TableCell>
                    <TableCell>Информация</TableCell>
                    <TableCell align="center">Действия</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                            {user.username.charAt(0).toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {user.username}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {user.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getRoleText(user.role)}
                          color={getRoleColor(user.role)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusText(user.is_active)}
                          color={getStatusColor(user.is_active)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(user.updated_at)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(user.created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {user.telegram_id && (
                            <Chip
                              label={`TG: ${user.telegram_id}`}
                              size="small"
                              variant="outlined"
                              color="primary"
                            />
                          )}
                          {user.is_premium && (
                            <Chip
                              label="Premium"
                              size="small"
                              variant="outlined"
                              color="secondary"
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Редактировать">
                            <IconButton
                              size="small"
                              onClick={() => handleEditUser(user)}
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={user.is_active ? 'Заблокировать' : 'Разблокировать'}>
                            <IconButton
                              size="small"
                              onClick={() => handleToggleUserStatus(user.id, user.is_active)}
                              color={user.is_active ? 'error' : 'success'}
                            >
                              {user.is_active ? <Block /> : <CheckCircle />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Удалить">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteUser(user.id)}
                              color="error"
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Диалог редактирования пользователя */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingUser ? 'Редактировать пользователя' : 'Добавить пользователя'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              margin="normal"
              label="Имя пользователя"
              value={formData.username}
              onChange={(e) => setFormData({...formData, username: e.target.value})}
              required
            />
            <TextField
              fullWidth
              margin="normal"
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              required
            />
            <TextField
              fullWidth
              margin="normal"
              label="Имя"
              value={formData.first_name}
              onChange={(e) => setFormData({...formData, first_name: e.target.value})}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Фамилия"
              value={formData.last_name}
              onChange={(e) => setFormData({...formData, last_name: e.target.value})}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Telegram ID"
              type="number"
              value={formData.telegram_id}
              onChange={(e) => setFormData({...formData, telegram_id: e.target.value})}
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Роль</InputLabel>
              <Select
                value={formData.role}
                onChange={(e) => setFormData({...formData, role: e.target.value})}
                label="Роль"
              >
                <MenuItem value="user">Пользователь</MenuItem>
                <MenuItem value="moderator">Модератор</MenuItem>
                <MenuItem value="admin">Администратор</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal">
              <InputLabel>Локаль</InputLabel>
              <Select
                value={formData.locale}
                onChange={(e) => setFormData({...formData, locale: e.target.value})}
                label="Локаль"
              >
                <MenuItem value="ru">Русский</MenuItem>
                <MenuItem value="en">English</MenuItem>
                <MenuItem value="ar">العربية</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal">
              <InputLabel>Часовой пояс</InputLabel>
              <Select
                value={formData.timezone}
                onChange={(e) => setFormData({...formData, timezone: e.target.value})}
                label="Часовой пояс"
              >
                <MenuItem value="Europe/Moscow">Москва</MenuItem>
                <MenuItem value="UTC">UTC</MenuItem>
                <MenuItem value="Asia/Dubai">Дубай</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal">
              <InputLabel>Мазхаб</InputLabel>
              <Select
                value={formData.madhab}
                onChange={(e) => setFormData({...formData, madhab: e.target.value})}
                label="Мазхаб"
              >
                <MenuItem value="hanafi">Ханафи</MenuItem>
                <MenuItem value="maliki">Малики</MenuItem>
                <MenuItem value="shafi">Шафии</MenuItem>
                <MenuItem value="hanbali">Ханбали</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal">
              <InputLabel>Статус</InputLabel>
              <Select
                value={formData.is_active ? 'active' : 'blocked'}
                onChange={(e) => setFormData({...formData, is_active: e.target.value === 'active'})}
                label="Статус"
              >
                <MenuItem value="active">Активен</MenuItem>
                <MenuItem value="blocked">Заблокирован</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_premium}
                  onChange={(e) => setFormData({...formData, is_premium: e.target.checked})}
                />
              }
              label="Premium пользователь"
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Отмена</Button>
          <Button
            variant="contained"
            onClick={handleSaveUser}
            disabled={loading}
          >
            {loading ? 'Сохранение...' : (editingUser ? 'Сохранить' : 'Создать')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Users;
